import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { entitySelected, isEntityActive } from '@/app/store/selectedEntitySlice';
import type { EntityDetail } from '@/features/authentication';
import { Toast } from '@/shared/components/Toast';
import { useAnchoredPopup } from '@/shared/hooks/useAnchoredPopup';
import { ENTITY_INACTIVE_MESSAGE } from '@/shared/services/api/entityInactive';
import { toMediaUrl } from '@/shared/utils/mediaUrl';
import { landingAfterEntitySwitch } from '../../utils/entitySwitchLanding';

interface SidebarEntitiesProps {
  entities: readonly EntityDetail[];
  isCollapsed: boolean;
}

/* The sidebar's own width, so the panel lines up with the rail it drops out of. */
const PANEL_WIDTH = 236;
const ROW_HEIGHT = 56;
const PANEL_PADDING = 12;
const PANEL_MAX_HEIGHT = 320;

/** Above the sidebar and the header, below any dialog. */
const PANEL_Z_INDEX = 70;

/**
 * The sidebar's header: the legal entities the signed-in user is assigned to, as a dropdown.
 *
 * <h2>It is the header, not a row under one</h2>
 *
 * It sat below the organization row, under an "ENTITIES" heading. Both are gone: the row showed
 * `DEFAULT_ORG` for every role but Mega Admin — a bundled name and badge, constant and therefore wrong
 * for a second tenant — and stacking it above a real, fetched entity said the same kind of thing twice.
 * This now occupies that position, styled as that row was.
 *
 * Mega Admin keeps the organization row instead: it holds no assignments by design, so there is nothing
 * for this control to show. `Sidebar` picks between the two.
 *
 * <h2>Where the list comes from</h2>
 *
 * `entityDetail` on `GET /users/me` — the profile the application already fetches at sign-in, so the
 * control costs no request of its own. Each entry carries its own `entityLogo` (`ole_entity_logo`, a
 * base64 data URI), so every row shows the entity's badge beside its name rather than one badge for all.
 *
 * The same assignment rows back `/users/me/assigned-hierarchy`, which the hierarchy masters read when
 * they need the entities grouped by organization; this is the flat form, because a dropdown has no use
 * for which organization each sits under.
 *
 * <h2>What selecting does</h2>
 *
 * It sets the entity the whole application is scoped to. The choice goes into `selectedEntitySlice`, the
 * API client sends it as `X-Entity-Id` on every request, and the platform narrows `AssignedEntityScope`
 * to it — the one place Business Unit, Division, Department, Location, Legal Entity and Workflow Config
 * already scope by. `Layout` remounts the routed screen when it changes, so what is on screen is
 * refetched rather than left showing the entity the user has just navigated away from.
 *
 * The selection can only ever pick from the user's own assignments, which is what this list is; the
 * platform refuses a header naming anything else rather than trusting it.
 *
 * <h2>It floats; it does not push the menu down</h2>
 *
 * The list is portalled to `document.body` and placed `position: fixed` from the trigger's measured box
 * by `useAnchoredPopup` — the same hook the RBAC role picker uses, so the two behave identically. It was
 * an inline `<ul>`, which took layout space and shoved Dashboard, Templates and the rest of the
 * navigation down the rail every time it opened.
 *
 * <h2>Nothing is drawn when there is nothing to draw</h2>
 *
 * An empty list renders no control at all — an empty dropdown reads as a screen that failed to load.
 *
 * <h2>Collapsed, it is the badge alone</h2>
 *
 * The rail keeps its header: the selected entity's badge, centred, in a row the same height as the expanded
 * one, with the name in its tooltip and the list still a click away. It used to draw nothing here, so the
 * logo vanished and the navigation jumped up under the content header.
 */
export function SidebarEntities({ entities, isCollapsed }: SidebarEntitiesProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const selectedId = useAppSelector((state) => state.selectedEntity.id);
  /*
    "Please contact your administrator." after an Inactive entity was chosen (2026-10-10), or null. Shown as the
    application's red error toast (2026-10-15), which times itself out -- it was a line under the trigger.
  */
  const [refusal, setRefusal] = useState<string | null>(null);
  const dismissRefusal = useCallback(() => {
    setRefusal(null);
  }, []);

  /*
    An entity chosen on a screen that has to be left first (2026-10-29): selected once the screen it lands on is the one
    drawn. The data router commits a navigation as a transition, after its promise settles, while the selection
    renders at once -- so selecting any earlier drew the old screen under the new entity, which flashed "The form did
    not load" on a Supplier's Edit. A navigation that is blocked (a BID's unsaved-changes prompt) or goes elsewhere
    drops the choice.
  */
  const [pending, setPending] = useState<{ entity: EntityDetail; from: string; landing: string } | null>(null);
  useEffect(() => {
    if (pending === null || pathname === pending.from) return;
    if (pathname === pending.landing) dispatch(entitySelected(pending.entity));
    setPending(null);
  }, [dispatch, pathname, pending]);

  /*
    Dismissal is the hook's: an outside press, Escape, or the trigger leaving the viewport. A scroll
    re-measures rather than closing, so the panel follows the rail instead of vanishing under the thumb.
  */
  const { open, style, popupRef, toggle, close } = useAnchoredPopup({
    width: PANEL_WIDTH,
    /* Capped, so a long assignment list scrolls rather than growing past the viewport. */
    height: Math.min(PANEL_MAX_HEIGHT, entities.length * ROW_HEIGHT + PANEL_PADDING),
    align: 'start',
    zIndex: PANEL_Z_INDEX,
  });

  if (entities.length === 0) return null;

  /*
    The first entity is what the trigger shows until one is chosen. `find` rather than an index, so a
    profile that reloads with a different set cannot leave the trigger naming an entity that is gone.
  */
  const selected = entities.find((entity) => entity.id === selectedId) ?? entities[0];
  if (!selected) return null;

  return (
    <div className="sidebar__entities">
      <Tooltip target=".sidebar__entities [data-pr-tooltip]" position="right" />
      <button
        type="button"
        className="sidebar__entity-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggle}
        data-pr-tooltip={selected.name}
      >
        <span className="sidebar__entity-left">
          <EntityBadge entity={selected} />
          {isCollapsed ? (
            <span className="visually-hidden">{selected.name}</span>
          ) : (
            <span className="sidebar__entity-name" data-pr-tooltip={selected.name}>{selected.name}</span>
          )}
        </span>
        {!isCollapsed && (
          <i
            className={`pi pi-chevron-down sidebar__entity-chevron${open ? ' is-open' : ''}`}
            aria-hidden="true"
          />
        )}
      </button>

      {open
        ? createPortal(
          <div
            ref={popupRef}
            className="sidebar__entity-panel"
            role="listbox"
            aria-label="Assigned entities"
            style={style}
          >
            {entities.map((entity) => {
              const on = entity.id === selected.id;
              const inactive = !isEntityActive(entity);

              return (
                <button
                  key={entity.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={`sidebar__entity-option${on ? ' is-on' : ''}${inactive ? ' is-inactive' : ''}`}
                  data-pr-tooltip={entity.name}
                  onClick={() => {
                    close();
                    /*
                      Listed, never worked in (2026-10-10): an Inactive entity is refused with the platform's own
                      words and the current entity stays selected -- nothing reloads.
                    */
                    if (inactive) {
                      setRefusal(ENTITY_INACTIVE_MESSAGE);
                      return;
                    }
                    setRefusal(null);
                    /*
                      A create from the previous entity's template goes back to its picker, a record's View or Edit to
                      its list (2026-10-29) -- and the entity is selected once that screen is drawn (see `pending`).
                    */
                    const landing = on ? null : landingAfterEntitySwitch(pathname);
                    if (landing === null) {
                      setPending(null);
                      dispatch(entitySelected(entity));
                      return;
                    }
                    setPending({ entity, from: pathname, landing });
                    void navigate(landing, { replace: true });
                  }}
                >
                  <EntityBadge entity={entity} />
                  <span className="sidebar__entity-name">{entity.name}</span>
                  {on ? <i className="pi pi-check sidebar__entity-tick" aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>,
          document.body,
        )
        : null}

      {refusal !== null ? <Toast message={refusal} tone="error" onDismiss={dismissRefusal} /> : null}
    </div>
  );
}

/**
 * One entity's badge, or its initial where it has none.
 *
 * `toMediaUrl` is the same guard the organization row uses: a data URI or a URL renders, and a bare file
 * name or an empty string does not — the browser would answer those with a broken-image icon, which
 * reads as "this failed" rather than "there is no logo". The initial is the honest fallback, and it keeps
 * every row the same height whether or not a logo was uploaded. A logo that is a URL but fails to load
 * falls back to the initial too.
 */
function EntityBadge({ entity }: { entity: EntityDetail }) {
  const src = toMediaUrl(entity.entityLogo);
  /* The source that failed, so a new logo for the same entity is tried again. */
  const [failed, setFailed] = useState<string | null>(null);

  if (src && src !== failed) {
    return (
      <img
        className="sidebar__entity-logo"
        src={src}
        alt=""
        onError={() => {
          setFailed(src);
        }}
      />
    );
  }
  return (
    <span className="sidebar__entity-logo sidebar__entity-logo--initial" aria-hidden="true">
      {entity.name.trim().charAt(0).toUpperCase()}
    </span>
  );
}
